import React, { useCallback, useEffect, useRef, useState } from "react";
import swal from "sweetalert";
import { Link } from "react-router-dom";
import {
  Button,
  Col,
  Dropdown,
  DropdownButton,
  FormControl,
  InputGroup,
  Row,
} from "react-bootstrap";
import { AddIconOutline, MenuDots, SearchIcon } from "../../components/SvgIcons";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { customStyles } from "../../utils/constants";
import DataTable from "react-data-table-component";
import BarLoader from "react-spinners/BarLoader";
import { EmptyLocalStorage } from "../../services/auth/auth.service";
import { DeleteCounty, GetCountyList } from "../../services/county.service";
import {
  LIST_PAGE_SIZE,
  LIST_TABLE_SCROLL_HEIGHT,
  useDebouncedValue,
  useListPageScrollLock,
} from "../../hooks/useInfinitePagedList";
import { ListLoadMoreIndicator } from "../../components/ListInfiniteHelpers";

function Counties() {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allRows, setAllRows] = useState([]);
  const [visibleRows, setVisibleRows] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const debouncedKeyword = useDebouncedValue(keyword, 500);
  const pageRef = useRef(1);
  const filteredRef = useRef([]);
  const loadingMoreRef = useRef(false);
  const tableWrapperRef = useRef(null);

  useListPageScrollLock();

  const clearInput = () => setKeyword("");

  const getCounties = async () => {
    setLoading(true);
    try {
      const result = await GetCountyList(null);
      if (result?.success === false) {
        setAllRows([]);
        setVisibleRows([]);
        setHasMore(false);
        setLoading(false);
        return toast.error(result.message || "Failed to load counties");
      }

      const list = result.list || [];
      const rowData = list.map((dt, index) => ({
        id: dt.id,
        index: index + 1,
        name: dt.name || "-",
        description: dt.description || "-",
        is_active: dt.is_active,
        status: dt.is_active === false ? "Inactive" : "Active",
        raw: dt,
      }));
      setAllRows(rowData);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setAllRows([]);
      setVisibleRows([]);
      if (error.response?.status == 401) {
        EmptyLocalStorage();
        history.push("/");
      } else {
        toast.error(error.response?.data?.message || "Failed to load counties");
      }
    }
  };

  useEffect(() => {
    getCounties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Client-side filter + chunked display (30 at a time)
  useEffect(() => {
    const search = debouncedKeyword.trim().toLowerCase();
    const filtered = !search
      ? allRows
      : allRows.filter(
          (row) =>
            (row.name || "").toLowerCase().includes(search) ||
            (row.description || "").toLowerCase().includes(search) ||
            (row.status || "").toLowerCase().includes(search)
        );

    filteredRef.current = filtered;
    pageRef.current = 1;
    const firstChunk = filtered.slice(0, LIST_PAGE_SIZE);
    setVisibleRows(firstChunk);
    setFilteredTotal(filtered.length);
    setHasMore(filtered.length > firstChunk.length);
  }, [allRows, debouncedKeyword]);

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    // Keep a tiny delay so the indicator is visible
    window.setTimeout(() => {
      const nextPage = pageRef.current + 1;
      const nextCount = nextPage * LIST_PAGE_SIZE;
      const nextRows = filteredRef.current.slice(0, nextCount);
      pageRef.current = nextPage;
      setVisibleRows(nextRows);
      setHasMore(nextRows.length < filteredRef.current.length);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }, 150);
  }, [hasMore]);

  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;

    const getScrollEl = () => wrapper.querySelector(".rdt_TableBody");
    let scrollEl = getScrollEl();
    let tries = 0;

    const onScroll = () => {
      if (!scrollEl || loadingMoreRef.current || !hasMore) return;
      const remaining =
        scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
      if (remaining < 80) loadMore();
    };

    const attach = () => {
      scrollEl = getScrollEl();
      if (!scrollEl) {
        if (tries < 20) {
          tries += 1;
          requestAnimationFrame(attach);
        }
        return;
      }
      scrollEl.addEventListener("scroll", onScroll, { passive: true });
    };

    attach();
    return () => {
      if (scrollEl) scrollEl.removeEventListener("scroll", onScroll);
    };
  }, [loadMore, hasMore, visibleRows.length]);

  const onCountyDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "Are you sure you want to delete this county?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;
      try {
        const result = await DeleteCounty(id);
        if (result.success) {
          swal(result.message || "County deleted successfully", {
            icon: "success",
          });
          getCounties();
        } else {
          toast.error(result.message || "Failed to delete county");
        }
      } catch (error) {
        if (error.response?.status == 401) {
          EmptyLocalStorage();
          history.push("/");
        } else {
          toast.error(
            error.response?.data?.message || "Failed to delete county"
          );
        }
      }
    });
  };

  const onEdit = (data) => {
    history.push("/manage-counties/edit-county/" + data.id, {
      data: data.raw || data,
    });
  };

  const countyActions = (data) => (
    <DropdownButton
      align="end"
      className={"actionDropdown"}
      id="dropdown-item-button"
      title={<MenuDots />}
    >
      <Dropdown.Item as="button" onClick={() => onEdit(data)}>
        Edit
      </Dropdown.Item>
      <Dropdown.Item as="button" onClick={() => onCountyDelete(data.id)}>
        Delete
      </Dropdown.Item>
    </DropdownButton>
  );

  const columns = [
    {
      id: "name",
      name: "County Name",
      selector: (rowData) => rowData.name,
      sortable: true,
    },
    {
      id: "description",
      name: "Description",
      selector: (rowData) => rowData.description,
      wrap: true,
    },
    {
      id: "status",
      name: "Status",
      selector: (rowData) => rowData.status,
      format: (rowData) => (
        <div
          className={
            rowData.is_active === false
              ? "status-badge status-inactive"
              : "status-badge status-active"
          }
        >
          {rowData.status}
        </div>
      ),
    },
    {
      id: "action",
      name: "Action",
      cell: (rowData) => countyActions(rowData),
      width: "100px",
    },
  ];

  return (
    <div className={"usersPage users-page-fixed"}>
      <h3 className="page-heading">Manage Counties</h3>
      <hr />
      <div className={"customOrderFilters users-page-filters"}>
        <Row>
          <Col xs={12} sm={12} md={5} lg={5} xl={5}>
            <InputGroup className="mb-3 search-group">
              <InputGroup.Text id="basic-addon1">
                <SearchIcon />
              </InputGroup.Text>
              <FormControl
                placeholder="Search"
                className="custom-search"
                aria-label="Search"
                maxLength={"255"}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                style={{
                  borderRight: keyword ? "none" : "1px solid #ced4da",
                  borderTopRightRadius: keyword ? "0" : "5px",
                  borderBottomRightRadius: keyword ? "0" : "5px",
                }}
              />
              {keyword && (
                <InputGroup.Text
                  className="cross-icon"
                  id="basic-addon2"
                  onClick={clearInput}
                >
                  <svg
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 6.48828L18 18.4883"
                      stroke="#817A8E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18 6.48828L6 18.4883"
                      stroke="#817A8E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </InputGroup.Text>
              )}
            </InputGroup>
          </Col>
          <Col xs={12} sm={12} md={7} lg={7} xl={7}>
            <Row>
              <Col xs={12} sm={12} md={3} lg={3} xl={3}></Col>
              <Col xs={12} sm={12} md={5} lg={5} xl={5}></Col>
              <Col xs={12} sm={12} md={6} lg={4} xl={4}>
                <Link to={"/manage-counties/add-county"}>
                  <Button className={"btn btn-green-theme h40 w-100"}>
                    <AddIconOutline /> Add New
                  </Button>
                </Link>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      <div
        className="theme-dataGrid products-dataGrid custom-table-border mt-3 user-table users-infinite-table"
        ref={tableWrapperRef}
      >
        <DataTable
          columns={columns}
          data={visibleRows}
          progressPending={loading}
          customStyles={customStyles}
          selectableRows={false}
          striped
          highlightOnHover
          pagination={false}
          persistTableHead
          fixedHeader
          fixedHeaderScrollHeight={LIST_TABLE_SCROLL_HEIGHT}
          noDataComponent={
            <div
              style={{
                padding: "48px 16px",
                textAlign: "center",
                width: "100%",
                color: "#6c757d",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              No Counties Found
            </div>
          }
          progressComponent={
            <BarLoader
              color={"#961b1a"}
              loading={loading}
              css={"marginTop: 10px"}
              height={"4"}
              width={"100%"}
            />
          }
        />
        <ListLoadMoreIndicator
          loadingMore={loadingMore}
          hasMore={hasMore}
          totalRows={filteredTotal}
          entityLabel="counties"
        />
      </div>
    </div>
  );
}

export default Counties;
